<div class="page-content-container">
	<div class="page-content">
		@if (!hasConfirmedSelection) {
			<div class="property-setup-container">
				<h3 class="header-text header-margin-bottom" i18n="@@addClientPropertySetup-clientPropertySetup">
					Client Property Setup
				</h3>
			</div>
		}
		@if (!hasConfirmedSelection) {
			<mat-divider></mat-divider>
		}

		<!-- Property Selection Type -->
		@if (!hasConfirmedSelection) {
			<div class="property-selection-container">
				<mat-radio-group
					class="thin-outline-radio-button-group two-col-radio-group"
					[(ngModel)]="selectedType"
					required
					data-automation-id="property-selection-group"
				>
					<mat-radio-button [value]="PropertySelectionType.newProperty" data-automation-id="add-new-property-option">
						<div class="radio-button-content">
							<div class="radio-button-text">
								<h3 i18n="@@addClientPropertySetup-addNewProperty">Add New Property</h3>
								<p i18n="@@addClientPropertySetup-addNewPropertyDescription">
									Create a new property for this client's residence
								</p>
							</div>
							<mat-icon svgIcon="sl-add-property" class="radio-button-icon"></mat-icon>
						</div>
					</mat-radio-button>
					<mat-radio-button
						[value]="PropertySelectionType.existing"
						data-automation-id="select-existing-property-option"
					>
						<div class="radio-button-content">
							<div class="radio-button-text">
								<h3 i18n="@@addClientPropertySetup-selectExistingProperty">Select an Existing Property</h3>
								<p i18n="@@addClientPropertySetup-selectExistingPropertyDescription">
									Link this client to a property already in your system
								</p>
							</div>
							<mat-icon svgIcon="sl-select-existing-property" class="radio-button-icon"></mat-icon>
						</div>
					</mat-radio-button>
				</mat-radio-group>
			</div>
		}

		<!-- New Property Details -->
		@if (propertySelectionType === PropertySelectionType.newProperty) {
			<div class="new-property-section">
				<div class="new-property-header-container">
					<h3 class="header-text" i18n="@@addClientPropertySetup-newProperty">New Property</h3>
				</div>
				<mat-divider></mat-divider>
				<form [formGroup]="addClientWithNewPropertyForm">
					<div class="new-property-detail">
						<div class="new-property-column property-name">
							<mat-form-field
								appearance="outline"
								subscriptSizing="dynamic"
								class="text-field"
								[hideRequiredMarker]="true"
							>
								<mat-label class="mat-label-color" i18n="@@addClientPropertySetup-propertyName"
									>Property Name</mat-label
								>
								<input
									matInput
									formControlName="propertyName"
									maxLength="100"
									data-automation-id="property-name-input"
								/>
							</mat-form-field>
						</div>
						<div class="new-property-column timezone">
							<mat-form-field subscriptSizing="dynamic" class="column" appearance="outline" [hideRequiredMarker]="true">
								<mat-label class="mat-label-color" i18n="@@addPropertyDetails-Timezone">Timezone</mat-label>
								<input
									type="text"
									aria-label="Timezone"
									matInput
									[matAutocomplete]="timezone"
									[formControl]="addClientWithNewPropertyForm.controls.timezone"
									(input)="filterTimezones($event)"
									(click)="openAutocompleteTimezones()"
									data-automation-id="timezone-input"
								/>
								<mat-autocomplete
									#timezone="matAutocomplete"
									[displayWith]="displayTimezoneName"
									(optionSelected)="timezoneSelected()"
									data-automation-id="timezone-picker"
								>
									@for (timezone of filteredTimezones | async; track timezone.timezone) {
										<mat-option [value]="timezone" [attr.data-automation-id]="'timezone-option-' + timezone.timezone">
											{{ timezone.timezone }} - {{ timezone.description }}
										</mat-option>
									}
								</mat-autocomplete>
								@if (addClientWithNewPropertyForm.controls.timezone.hasError('notValid')) {
									<mat-hint i18n="@@addPropertyDetails-TimezoneError" data-automation-id="timezone-error">
										Please select a Timezone from the list
									</mat-hint>
								}
							</mat-form-field>
						</div>
						<div class="new-property-column address">
							<mat-form-field
								appearance="outline"
								subscriptSizing="dynamic"
								class="text-field"
								[hideRequiredMarker]="true"
							>
								<mat-label class="mat-label-color" i18n="@@addClientPropertySetup-address">Address</mat-label>
								<input matInput formControlName="address" maxLength="100" data-automation-id="address-input" />
							</mat-form-field>
						</div>
						<div class="new-property-column apartment">
							<mat-form-field
								appearance="outline"
								subscriptSizing="dynamic"
								class="text-field"
								[hideRequiredMarker]="true"
							>
								<mat-label class="mat-label-color" i18n="@@addClientPropertySetup-apartment"
									>Apartment/Suite (optional)</mat-label
								>
								<input matInput formControlName="apartment" maxLength="100" data-automation-id="apartment-input" />
							</mat-form-field>
						</div>
						<div class="new-property-column town">
							<mat-form-field
								appearance="outline"
								subscriptSizing="dynamic"
								class="text-field"
								[hideRequiredMarker]="true"
							>
								<mat-label class="mat-label-color" i18n="@@addClientPropertySetup-town">Town/City</mat-label>
								<input matInput formControlName="town" maxLength="100" data-automation-id="town-input" />
							</mat-form-field>
						</div>
						<div class="new-property-column state">
							<mat-form-field
								appearance="outline"
								subscriptSizing="dynamic"
								class="text-field"
								[hideRequiredMarker]="true"
							>
								<mat-label class="mat-label-color" i18n="@@addClientPropertySetup-state">State/Province</mat-label>
								<input matInput formControlName="state" maxLength="100" data-automation-id="state-input" />
							</mat-form-field>
						</div>
						<div class="new-property-column postal-code">
							<mat-form-field
								appearance="outline"
								subscriptSizing="dynamic"
								class="text-field"
								[hideRequiredMarker]="true"
							>
								<mat-label class="mat-label-color" i18n="@@addClientPropertySetup-postal-code"
									>Postal/Zip Code</mat-label
								>
								<input matInput formControlName="postalCode" maxLength="18" data-automation-id="postal-code-input" />
							</mat-form-field>
						</div>
						<div class="new-property-column country">
							<mat-form-field subscriptSizing="dynamic" class="column" appearance="outline" [hideRequiredMarker]="true">
								<mat-label class="mat-label-color" i18n="@@addPropertyDetails-Country">Country</mat-label>
								<input
									type="text"
									data-automation-id="country-input"
									aria-label="Country"
									matInput
									[matAutocomplete]="auto"
									[formControl]="addClientWithNewPropertyForm.controls.country"
									(input)="filterCountries($event)"
									(click)="openAutocompleteCountries()"
								/>
								<mat-autocomplete
									#auto="matAutocomplete"
									[displayWith]="displayCountryName"
									(optionSelected)="countrySelected()"
									data-automation-id="country-picker"
								>
									@for (country of filteredCountries | async; track country.twoLetterCode) {
										<mat-option [value]="country" [attr.data-automation-id]="'country-option-' + country.twoLetterCode">
											{{ country.name }}
										</mat-option>
									}
								</mat-autocomplete>
								@if (addClientWithNewPropertyForm.controls.country.hasError('notValid')) {
									<mat-hint i18n="@@addPropertyDetails-CountryError" data-automation-id="country-error">
										Please select a Country from the list
									</mat-hint>
								}
							</mat-form-field>
						</div>
					</div>
				</form>
			</div>
		}

		<!-- Existing Property Search -->
		@if (propertySelectionType === PropertySelectionType.existing) {
			<div class="existing-property-section">
				<div class="existing-property-header-container">
					<h3 class="header-text header-margin-bottom" i18n="@@addClientPropertySetup-existingProperty">
						Existing Property
					</h3>
				</div>
				<mat-divider></mat-divider>
				<mat-form-field appearance="outline" class="property-search-field thin-input-text">
					<mat-icon matPrefix svgIcon="sl-search"></mat-icon>
					<input
						matInput
						[formControl]="propertySearchControl"
						[matAutocomplete]="auto"
						placeholder="Search Property"
						i18n-placeholder="@@addClientPropertySetup-searchProperty"
						data-automation-id="property-search-input"
					/>
					<mat-icon matSuffix class="expand-icon">expand_more</mat-icon>
				</mat-form-field>
				<mat-autocomplete
					#auto="matAutocomplete"
					[displayWith]="displayPropertyName.bind(this)"
					(optionSelected)="onPropertySelected($event.option.value)"
				>
					@for (property of filteredProperties | async; track property.property.id) {
						<mat-option
							[value]="property"
							class="full-width-options"
							[attr.data-automation-id]="'property-option-' + property.property.id"
						>
							<div class="property-option">
								<span class="property-name">{{ property.property.name }}</span>
								<span class="property-address">{{ formatAddress(property) }}</span>
							</div>
						</mat-option>
					}
				</mat-autocomplete>
				<!-- Selected Property Details -->
				@if (showPropertyDetails && selectedProperty) {
					<div class="property-details-card" data-automation-id="selected-property-details">
						<div class="detail-row">
							<div class="detail-item">
								<span class="detail-label" i18n="@@addClientPropertySetup-propertyName">Property Name</span>
								<span class="detail-value">{{ selectedProperty.property.name }}</span>
							</div>
							<div class="detail-item">
								<span class="detail-label" i18n="@@addClientPropertySetup-address">Address</span>
								<span class="detail-value">{{ selectedProperty.property.address.address1 }}</span>
							</div>
							<div class="detail-item">
								<span class="detail-label" i18n="@@addClientPropertySetup-apartmentSuite">Apartment/Suite</span>
								<span class="detail-value">{{ selectedProperty.property.address.address2 || '' }}</span>
							</div>
							<div class="detail-item">
								<span class="detail-label" i18n="@@addClientPropertySetup-townCity">Town/City</span>
								<span class="detail-value">{{ selectedProperty.property.address.locality }}</span>
							</div>
						</div>
						<div class="detail-row">
							<div class="detail-item">
								<span class="detail-label" i18n="@@addClientPropertySetup-stateProvince">State/Province</span>
								<span class="detail-value">{{ selectedProperty.property.address.stateProvinceRegion || '' }}</span>
							</div>
							<div class="detail-item">
								<span class="detail-label" i18n="@@addClientPropertySetup-postalZipCode">Postal/Zip Code</span>
								<span class="detail-value">{{ selectedProperty.property.address.postalCode || '' }}</span>
							</div>
							<div class="detail-item">
								<span class="detail-label" i18n="@@addClientPropertySetup-country">Country</span>
								<span class="detail-value">{{ selectedProperty.property.address.countryCode || '' }}</span>
							</div>
							<div class="detail-item">
								<span class="detail-label" i18n="@@addClientPropertySetup-timezone">Timezone</span>
								<span class="detail-value">{{ selectedProperty.property.timezone || '' }}</span>
							</div>
						</div>
					</div>
				}
			</div>
		}
	</div>
</div>
